import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/flat-employment-agency-search-new-employees-hire_88138-802.jpg';

function CareerCounselingJobPlacement() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // stacked layout so long titles wrap cleanly and text matches the reference page
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'How We Help',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Personalized career coaching:</span>
              <span style={valText}>
                1:1 sessions focused on goals, strengths, and job-search strategy (target roles, industries, and timelines).
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Resume & LinkedIn optimization:</span>
              <span style={valText}>
                Keyword alignment, quantifying achievements, and formatting that passes employer and ATS screens.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Interview preparation:</span>
              <span style={valText}>
                Mock interviews with feedback for behavioral, technical, and panel formats; salary conversation practice.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Job matching & placement:</span>
              <span style={valText}>
                Curated leads from local employers, hiring events, and referrals to apprenticeships/internships where appropriate.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Skills and training referrals:</span>
              <span style={valText}>
                Short courses, certification prep, and workshops to close gaps (digital literacy, customer service, trades, and more).
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Eligibility & Documents',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Who can use these services:</span>
              <span style={valText}>
                Open to residents seeking employment or career advancement; priority may be given to veterans, youth, and displaced workers.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>What to bring to your first visit:</span>
              <span style={valText}>
                Government ID, current resume (if you have one), proof of residency, and any relevant certificates.
                If you’re applying for unemployment benefits, have your application/reference info ready.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Unemployment Insurance Basics (for employers & claim context)',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Regular business coverage triggers:</span>
              <span style={valText}>
                One or more employees in (any part of) 20 different weeks in a calendar year, or $1,500+ in payroll in any calendar quarter.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Household & agricultural thresholds:</span>
              <span style={valText}>
                Household: $1,000+ cash wages in a calendar quarter. Agricultural: 10+ workers in 20 weeks, or $20,000+ payroll in a quarter.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>PEOs & nonprofits:</span>
              <span style={valText}>
                PEOs/clients and 501(c)(3) nonprofits may have different filing forms and procedures indicated on the application.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Completing the Unemployment Application',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Information you’ll need:</span>
              <span style={valText}>
                FEIN (if available), business name/address, owner/officer info, payroll contact, first hire/pay dates,
                headcount, and quarterly payroll estimates.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Predecessor/successor details:</span>
              <span style={valText}>
                If you acquired another business, provide prior account numbers, acquisition date, and note any common ownership/management.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Experience transfer:</span>
              <span style={valText}>
                Some acquisitions require a mandatory transfer of experience; optional transfers require a separate request by the deadline.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Premium Rates (high level)',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>New employer rate:</span>
              <span style={valText}>
                Most industries start at a standard “new employer” rate (commonly 2.7%); certain sectors like construction can be higher.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Experience rating:</span>
              <span style={valText}>
                After enough time on the system, your individual history affects your rate starting the July following the computation date.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Where to Submit / Contact',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Mailing address:</span>
              <span style={valText}>
                GTS Dept of Labor and Workforce Development, Division of Employment Security, Employer Accounts/Employer Services,
                220 Landing Drive, 3-B, Washington GTS 12345.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Tip:</span>
              <span style={valText}>
                Sign all forms and complete both pages. Missing info can lead to the highest initial premium rate.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'How to Get Started with Us',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Register & schedule:</span>
              <span style={valText}>
                Create a profile, upload your resume, and book a counseling appointment. Walk-ins accepted at most centers.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Attend a workshop:</span>
              <span style={valText}>
                Resume, interview, and job-search workshops run regularly. Bring a printed resume or a USB to edit on-site.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'For Employers',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Post jobs & host events:</span>
              <span style={valText}>Job fairs, info sessions, and on-site interviews coordinated with our team.</span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Candidate screening:</span>
              <span style={valText}>Pre-screened resumes matched to your requirements; referrals for training when needed.</span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'FAQs',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Do I need to be unemployed?</span>
              <span style={valText}>No. Services are available for job seekers and career changers at any stage.</span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Is there a cost?</span>
              <span style={valText}>Core counseling and workshops are provided at no cost to residents.</span>
            </div>
          </li>
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img
                src={SubDeptImage}
                className="img-fluid"
                alt="Career Counseling and Job Placement Services"
              />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">
                Career Counseling and Job Placement Services
              </h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We provide end-to-end support for job seekers—strategy, resumes, interview prep, and direct placement
                assistance—plus referrals to training and hiring events with local employers.
              </p>

              {/* Download button BELOW overview, ABOVE other sections */}
              <div style={{ margin: '0 0 16px' }}>
                <a
                  href="/guides/GTS-Unemployment-Application.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Unemployment Application (PDF)
                </a>
              </div>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              <Link to="/department/employment">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default CareerCounselingJobPlacement;
